import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { shareToWhatsApp } from "@/lib/share";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { STATUSES, StatusBadge, formatDateTime } from "@/lib/status";
import { ArrowLeft, Download, MessageCircle, Trash2, Loader2, Pencil, ShoppingCart, PackageCheck, Mail, Send, Globe, BellRing, Lock, FileText, History, Repeat, ArrowRight, UserCheck, Ban, RotateCcw, Shuffle, MessageSquare } from "lucide-react";
import MailComposeDialog from "@/components/MailComposeDialog";
import B2BOrderDialog from "@/components/B2BOrderDialog";
import OrderItemsDialog from "@/components/OrderItemsDialog";
import AlternativeOrderDialog from "@/components/AlternativeOrderDialog";
import LineNotesDialog from "@/components/LineNotesDialog";
import PurchaserSelectDialog from "@/components/PurchaserSelectDialog";
import ReminderDialog from "@/components/ReminderDialog";
import RequestChat from "@/components/RequestChat";
import RequestTimeline from "@/components/RequestTimeline";

export default function RequestDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const [b2bOpen, setB2bOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [purchaserDialogOpen, setPurchaserDialogOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [assignable, setAssignable] = useState([]);
  const [altDialog, setAltDialog] = useState({ open: false, index: -1 });
  const [notesDialog, setNotesDialog] = useState({ open: false, index: -1 });

  const load = async () => {
    try {
      const { data } = await api.get(`/requests/${id}`);
      setReq(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Yüklenemedi");
      navigate("/talepler");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { api.get("/users/assignable").then((r) => setAssignable(r.data)).catch(() => {}); }, []);

  if (!req) return <div className="text-slate-400">Yükleniyor...</div>;

  const canOrder = user?.role === "admin" || user?.role === "purchasing" || (user?.extra_roles || []).includes("purchasing");
  // A regular "user" may only act on their own request; staff (admin/purchasing/warehouse) act on all.
  const canModify = user?.role !== "user" || req.user_id === user?.id;
  // Per-item note threads are visible to owner + purchasing + admin (not warehouse-only).
  const canSeeLineNotes = canOrder || req.user_id === user?.id;
  const hasItems = !!(req.items && req.items.length > 0);
  const detailItems = hasItems ? req.items : [{
    product_code: req.product_code, product_name: req.product_name, quantity: req.quantity, unit: req.unit,
  }];
  const toggleOne = (idx) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(idx)) n.delete(idx); else n.add(idx);
    return n;
  });
  // Only items that CAN be supplied (and aren't already fulfilled by an alternative) are orderable.
  const availableIdx = hasItems ? req.items.map((_, i) => i).filter((i) => !req.items[i].unavailable && !req.items[i].alt_fulfilled) : [];
  const allSelected = hasItems && availableIdx.length > 0 && selected.size === availableIdx.length;
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () => setSelected((prev) => (prev.size >= availableIdx.length ? new Set() : new Set(availableIdx)));
  const afterOrder = () => { setSelected(new Set()); load(); };

  // Remove a wrongly-entered alternative (pending or ordered) from an item line.
  const removeAlternative = async (index) => {
    if (!window.confirm("Bu alternatif ürünü kaldırmak istediğinize emin misiniz?")) return;
    try {
      const { data } = await api.delete(`/requests/${req.id}/items/${index}/alternative`);
      setReq(data);
      toast.success("Alternatif ürün kaldırıldı");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İşlem başarısız");
    }
  };

  // Mark (or unmark) selected items as "tedarik edilemiyor" (cannot be supplied).
  const markUnavailable = async (indexes, unavailable) => {
    try {
      const { data } = await api.post(`/requests/${req.id}/mark-unavailable`, { item_indexes: indexes, unavailable });
      setReq(data);
      setSelected(new Set());
      toast.success(unavailable ? "Tedarik edilemiyor olarak işaretlendi" : "İşaret kaldırıldı — tedarik edilebilir");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İşlem başarısız");
    }
  };

  const pdfUrl = `${API}/requests/${req.id}/pdf?token=${localStorage.getItem("access_token") || ""}`;

  const waNumber = (req.supplier_phone || "").replace(/[^\d]/g, "");
  // Items marked "tedarik edilemiyor" are excluded from supplier-facing messages.
  const shareItems = (req.items && req.items.length > 0 ? req.items : [{
    product_code: req.product_code, product_name: req.product_name, quantity: req.quantity, unit: req.unit,
  }]).filter((it) => !it.unavailable && !it.alt_fulfilled);
  const itemList = shareItems.map((it, i) => `${i + 1}. ${it.product_code} — ${it.product_name} • ${it.quantity} ${it.unit}${it.note ? `\n   Not: ${it.note}` : ""}`).join("\n");
  // NOTE: 'Sebep' is intentionally NOT included in the supplier-facing message (internal-only).
  const waMessage = `Merhaba ${req.supplier_name},\n\n*${req.request_no}* numaralı sipariş talebimiz:\n${itemList}\n\nDetaylar ekteki PDF'tedir.`;
  const waUrl = waNumber
    ? `https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage)}`
    : `https://wa.me/?text=${encodeURIComponent(waMessage)}`;

  const setStatus = async (status) => {
    setUpdating(true);
    try {
      const { data } = await api.patch(`/requests/${req.id}/status`, { status });
      setReq(data);
      toast.success("Durum güncellendi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUpdating(false);
    }
  };

  const fetchPdfBlob = async () => {
    const { data } = await api.get(`/requests/${req.id}/pdf`, { responseType: "blob" });
    return new File([data], `talep-${req.request_no}.pdf`, { type: "application/pdf" });
  };

  // Sending via WhatsApp/Mail/B2B marks the whole request as "Sipariş Verildi".
  const markSent = async (channel) => {
    try {
      const { data } = await api.post(`/requests/${req.id}/mark-sent`, {
        channel,
        supplier_id: req.supplier_id,
        supplier_name: req.supplier_name,
      });
      setReq(data);
      toast.success("Sipariş Verildi olarak işaretlendi");
    } catch (_) {
      /* sharing already happened; status update is best-effort */
    }
  };

  const handleWhatsApp = async () => {
    let file = null;
    try {
      file = await fetchPdfBlob();
    } catch (_) {
      /* PDF unavailable — share text only */
    }
    const res = await shareToWhatsApp({
      text: waMessage,
      title: `Talep ${req.request_no}`,
      file,
      waUrl,
      downloadName: `talep-${req.request_no}.pdf`,
    });
    if (res === "cancelled") return;
    toast.success(res === "waurl" ? "WhatsApp açıldı" : "Paylaşım menüsü açıldı");
    await markSent("whatsapp");
  };

  const handleDelete = async () => {
    if (!window.confirm("Bu talebi silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/requests/${req.id}`);
      toast.success("Silindi");
      navigate("/talepler");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleAssignPurchasing = () => setPurchaserDialogOpen(true);

  const assign = async (uid) => {
    try {
      const { data } = await api.patch(`/requests/${req.id}/assign`, { assigned_to: uid || null });
      setReq(data);
      toast.success(uid ? "Sipariş hazırlayan atandı" : "Atama kaldırıldı");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Atama yapılamadı");
    }
  };

  return (
    <div className="max-w-3xl mx-auto fade-up">
      <Link to="/talepler" data-testid="back-to-list" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4 gap-1">
        <ArrowLeft className="h-4 w-4" /> Talepler
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">{req.request_no}</h1>
            <StatusBadge status={req.status} />
          </div>
          <p className="text-sm text-slate-500 mt-1">{formatDateTime(req.created_at)} • {req.user_name}</p>
        </div>

        <div className="flex gap-2 flex-wrap">
          {(user?.role === "admin" || (req.user_id === user?.id && req.status === "Oluşturuldu")) && (
            <Link to={`/talepler/${req.id}/duzenle`} data-testid="edit-request-btn">
              <Button variant="outline" className="h-11 gap-2">
                <Pencil className="h-4 w-4" /> Düzenle
              </Button>
            </Link>
          )}
          {(user?.role === "admin" || req.user_id === user?.id) && req.status === "Oluşturuldu" && (
            <Button
              data-testid="assign-purchasing-btn"
              variant="outline"
              onClick={handleAssignPurchasing}
              className="h-11 gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <ShoppingCart className="h-4 w-4" /> Satınalmacıya İlet
            </Button>
          )}
          {["Sipariş Verildi", "Kontrol Bekliyor", "Kısmi Geldi", "İade Bekleniyor", "Değişim Bekleniyor"].includes(req.status) && (
            <Link to={`/talepler/${req.id}/kontrol`} data-testid="receipt-check-btn">
              <Button className="h-11 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white">
                <PackageCheck className="h-4 w-4" /> Kontrol Et
              </Button>
            </Link>
          )}
          {req.status === "Kısmi Geldi" &&
           (req.receipt?.items || []).some((i) => !(i.done || i.resolved)) && (
            <Button
              data-testid="remind-supplier-btn"
              onClick={() => setReminderOpen(true)}
              variant="outline"
              className="h-11 gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <BellRing className="h-4 w-4" /> Hatırlat
            </Button>
          )}
          <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="download-pdf-btn">
            <Button variant="outline" className="h-11 gap-2">
              <Download className="h-4 w-4" /> PDF
            </Button>
          </a>
          {canModify && (<>
          <Button
            data-testid="b2b-share-btn"
            onClick={() => setB2bOpen(true)}
            variant="outline"
            className="h-11 gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800"
          >
            <Globe className="h-4 w-4" />
            B2B
          </Button>
          <Button
            data-testid="mail-share-btn"
            onClick={() => setMailOpen(true)}
            variant="outline"
            className="h-11 gap-2 border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
          >
            <Mail className="h-4 w-4" />
            Mail
          </Button>
          <Button
            data-testid="whatsapp-share-btn"
            onClick={handleWhatsApp}
            className="h-11 gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </Button>
          </>)}
        </div>
      </div>

      {(req.assigned_to_name || canModify) && (
        <div data-testid="assignee-bar" className="mb-5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-slate-700">
            <UserCheck className="h-4 w-4 text-slate-500 shrink-0" />
            <span>Sipariş Hazırlayan:</span>
            <span data-testid="assignee-name" className="font-semibold text-slate-900">{req.assigned_to_name || "Atanmadı"}</span>
          </div>
          {canModify && (
            <div className="sm:ml-auto w-full sm:w-64">
              <Select value={req.assigned_to || "__none__"} onValueChange={(v) => assign(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="assignee-select" className="h-9 bg-white">
                  <SelectValue placeholder="Ata" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Atama yok</SelectItem>
                  {assignable.map((u) => (
                    <SelectItem key={u.id} value={u.id} data-testid={`assignee-select-opt-${u.id}`}>
                      {u.name}{u.role === "warehouse" ? " · Depo" : u.role === "purchasing" ? " · Satın Alma" : u.role === "admin" ? " · Yönetici" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      {req.last_sent && (
        <div data-testid="last-sent-banner" className="mb-5 flex items-start gap-2.5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <Send className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
          <div className="text-sm text-blue-900">
            <span className="font-semibold">{req.last_sent.supplier_name}</span> tedarikçisine{" "}
            <span className="font-semibold">{req.last_sent.channel === "whatsapp" ? "WhatsApp" : "E-posta"}</span> ile gönderildi.
            <span className="text-blue-700/70"> • {formatDateTime(req.last_sent.at)} • {req.last_sent.by}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
        <Tabs defaultValue="detail" className="w-full">
          <TabsList className="grid w-full grid-cols-2 h-11 bg-slate-100">
            <TabsTrigger value="detail" data-testid="tab-detail" className="h-9 gap-1.5"><FileText className="h-4 w-4" /> Talep Detayı</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history" className="h-9 gap-1.5"><History className="h-4 w-4" /> Sipariş Geçmişi</TabsTrigger>
          </TabsList>
          <TabsContent value="detail" className="space-y-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
          <Section title="Tedarikçi">
            <Field label="Ad" value={req.supplier_name} />
            <Field label="Telefon" value={req.supplier_phone || "-"} />
          </Section>
          <Section title="Detaylar">
            <Field label="Sebep" value={req.reason} />
            <Field label="Talep Eden" value={req.user_name} />
            <Field label="Oluşturma" value={formatDateTime(req.created_at)} />
          </Section>
          <Section title={`Ürünler (${(req.items && req.items.length) || 1} kalem)`}>
            {canOrder && hasItems && (
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="order-select-all"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                    className="h-4 w-4 rounded border-slate-300 text-slate-900 cursor-pointer"
                  />
                  Tümünü seç
                </label>
                {selected.size > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      data-testid="order-selected-btn"
                      size="sm"
                      onClick={() => setOrderDialogOpen(true)}
                      className="h-9 gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      <ShoppingCart className="h-3.5 w-3.5" /> Seçili ({selected.size}) ürünü sipariş ver
                    </Button>
                    <Button
                      data-testid="mark-unavailable-btn"
                      size="sm"
                      variant="outline"
                      onClick={() => markUnavailable([...selected], true)}
                      className="h-9 gap-1.5 border-slate-300 text-slate-600 hover:bg-slate-100"
                    >
                      <Ban className="h-3.5 w-3.5" /> Tedarik edilemiyor
                    </Button>
                  </div>
                )}
              </div>
            )}
            <div className="border border-slate-100 rounded-md overflow-hidden">
              <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-600">
                {canOrder && hasItems && <div className="w-4 shrink-0" />}
                <div className="flex-1 min-w-0">Ürün</div>
                <div className="w-32 shrink-0 text-right">Tedarikçi</div>
              </div>
              {detailItems.map((it, idx) => {
                const unavailable = !!it.unavailable;
                const struck = unavailable || !!it.alt_fulfilled;
                return (
                <div key={idx} data-testid={`detail-item-${it.product_code}`} className={`flex items-start gap-2 px-3 py-2.5 border-t border-slate-100 first:border-t-0 sm:first:border-t text-sm ${struck ? "bg-slate-50" : ""}`}>
                  {canOrder && hasItems && (
                    <input
                      type="checkbox"
                      data-testid={`detail-item-select-${idx}`}
                      checked={selected.has(idx)}
                      disabled={unavailable || it.alt_fulfilled}
                      onChange={() => toggleOne(idx)}
                      className={`h-4 w-4 mt-1 rounded border-slate-300 text-slate-900 shrink-0 ${unavailable || it.alt_fulfilled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className={`font-medium ${struck ? "text-slate-400 line-through" : "text-slate-900"}`}>{it.product_code}</span>
                      <span className="text-xs text-slate-400">{it.quantity} {it.unit}</span>
                      {unavailable && <span data-testid={`detail-item-unavailable-badge-${idx}`} className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-200 text-slate-500">Tedarik edilemiyor</span>}
                      {it.alt_fulfilled && <span data-testid={`alt-fulfilled-badge-${idx}`} className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">Alternatif ile karşılandı</span>}
                    </div>
                    <div className={`leading-snug ${struck ? "text-slate-400 line-through" : "text-slate-700"}`}>{it.product_name}</div>
                    {it.note && <div data-testid={`detail-item-note-${it.product_code}`} className={`text-xs mt-0.5 ${struck ? "text-slate-400" : "text-amber-700"}`}>Not: {it.note}</div>}

                    {/* Alternative product for this line — ordered vs saved-pending */}
                    {it.alternative && (it.alternative.ordered ? (
                      <div data-testid={`alternative-${idx}`} className="mt-2 bg-indigo-50 border border-indigo-200 rounded-md px-2.5 py-2">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-indigo-800 mb-1">
                          <Shuffle className="h-3.5 w-3.5" /> Alternatif olarak sipariş verildi
                        </div>
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900">{it.alternative.product_code}</span>
                          <span className="text-xs text-slate-500">{it.alternative.quantity} {it.alternative.unit}</span>
                        </div>
                        <div className="text-xs text-slate-700 leading-snug">{it.alternative.product_name}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{it.alternative.supplier_name} • {formatDateTime(it.alternative.ordered_at)} • {it.alternative.ordered_by}</div>
                        {it.alternative.note && <div className="text-[11px] text-slate-500 mt-0.5">Not: {it.alternative.note}</div>}
                        {canOrder && (
                          <div className="flex items-center gap-3 mt-1.5">
                            <button data-testid={`alt-edit-btn-${idx}`} onClick={() => setAltDialog({ open: true, index: idx })} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 hover:underline"><Pencil className="h-3 w-3" /> Düzelt</button>
                            <button data-testid={`alt-remove-btn-${idx}`} onClick={() => removeAlternative(idx)} className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800 hover:underline"><Trash2 className="h-3 w-3" /> Sil</button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div data-testid={`alternative-${idx}`} className="mt-2 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-amber-800 mb-1">
                          <Shuffle className="h-3.5 w-3.5" /> Alternatif seçildi · sipariş bekliyor
                        </div>
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900">{it.alternative.product_code}</span>
                          <span className="text-xs text-slate-500">{it.alternative.quantity} {it.alternative.unit}</span>
                        </div>
                        <div className="text-xs text-slate-700 leading-snug">{it.alternative.product_name}</div>
                        {it.alternative.supplier_name && <div className="text-[11px] text-slate-500 mt-0.5">Önerilen tedarikçi: {it.alternative.supplier_name}</div>}
                        {it.alternative.note && <div className="text-[11px] text-slate-500 mt-0.5">Not: {it.alternative.note}</div>}
                        {canOrder && (
                          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                            <button
                              data-testid={`alt-place-order-btn-${idx}`}
                              onClick={() => setAltDialog({ open: true, index: idx })}
                              className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700"
                            >
                              <ShoppingCart className="h-3.5 w-3.5" /> Sipariş ver
                            </button>
                            <button data-testid={`alt-edit-btn-${idx}`} onClick={() => setAltDialog({ open: true, index: idx })} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 hover:underline"><Pencil className="h-3 w-3" /> Düzelt</button>
                            <button data-testid={`alt-remove-btn-${idx}`} onClick={() => removeAlternative(idx)} className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800 hover:underline"><Trash2 className="h-3 w-3" /> Sil</button>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Per-line actions */}
                    {hasItems && (canSeeLineNotes || (canOrder && !unavailable && !it.alt_fulfilled)) && (
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        {canOrder && !unavailable && !it.alt_fulfilled && !it.alternative && (
                          <button
                            data-testid={`alt-order-btn-${idx}`}
                            onClick={() => setAltDialog({ open: true, index: idx })}
                            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                          >
                            <Shuffle className="h-3 w-3" /> Alternatif ürün
                          </button>
                        )}
                        {canSeeLineNotes && (
                          <button
                            data-testid={`line-notes-btn-${idx}`}
                            onClick={() => setNotesDialog({ open: true, index: idx })}
                            className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 hover:underline"
                          >
                            <MessageSquare className="h-3 w-3" /> Not{(it.line_notes && it.line_notes.length) ? ` (${it.line_notes.length})` : ""}
                          </button>
                        )}
                      </div>
                    )}

                    {unavailable && canOrder && (
                      <button
                        data-testid={`detail-item-restore-${idx}`}
                        onClick={() => markUnavailable([idx], false)}
                        className="mt-1 inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                      >
                        <RotateCcw className="h-3 w-3" /> Tedarik edilebilir yap
                      </button>
                    )}
                  </div>
                  <div className="w-28 sm:w-32 shrink-0">
                    {unavailable ? <div className="text-right text-xs text-slate-300">—</div> : <ItemSupplierCell it={it} />}
                  </div>
                </div>
                );
              })}
            </div>
          </Section>
          {req.receipt && req.receipt.items && (
            <Section title={`Depo Kontrol Raporu — ${req.receipt.checked_by}`}>
              <div className="border border-slate-100 rounded-md overflow-hidden">
                {req.receipt.items.map((ri, idx) => {
                  const STATE_BADGE = {
                    ok: "bg-emerald-100 text-emerald-700",
                    missing: "bg-amber-100 text-amber-700",
                    wrong: "bg-rose-100 text-rose-700",
                    damaged: "bg-orange-100 text-orange-700",
                    returned: "bg-violet-100 text-violet-700",
                    exchange: "bg-teal-100 text-teal-700",
                  };
                  const STATE_LABEL = { ok: "Tam", missing: "Eksik", wrong: "Yanlış", damaged: "Hasarlı", returned: "İade", exchange: "Değişim" };
                  const token = localStorage.getItem("access_token") || "";
                  return (
                    <div key={idx} className="p-3 border-t border-slate-100 first:border-t-0 space-y-2">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 text-sm">{ri.product_code}</div>
                          <div className="text-xs text-slate-500 truncate">{ri.product_name}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-slate-500">
                            <span className="line-through">{ri.ordered_qty}</span> → <span className="font-bold text-slate-900">{ri.received_qty}</span> {ri.unit}
                          </span>
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATE_BADGE[ri.state]}`}>{STATE_LABEL[ri.state]}</span>
                        </div>
                      </div>
                      {ri.note && <div className="text-xs text-slate-700 bg-slate-50 px-2 py-1.5 rounded">{ri.note}</div>}
                      {ri.state === "exchange" && ri.exchange_to && (
                        <div data-testid={`detail-exchange-${ri.product_code}`} className="bg-teal-50 border border-teal-200 rounded-md px-2.5 py-2">
                          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-teal-800 mb-2">
                            <Repeat className="h-3.5 w-3.5" /> Ürün Değişimi
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-1.5 sm:gap-2 sm:items-stretch">
                            <div className="bg-white border border-teal-200 rounded px-2 py-1.5">
                              <div className="text-[10px] font-semibold uppercase text-slate-400">Gelen / yanlış ürün</div>
                              <div className="text-xs font-bold text-slate-900">{ri.product_code}</div>
                              <div className="text-[11px] text-slate-600 leading-snug">{ri.product_name}</div>
                            </div>
                            <div className="flex items-center justify-center text-teal-600">
                              <ArrowRight className="h-4 w-4 hidden sm:block" />
                              <span className="sm:hidden text-[10px] font-bold tracking-wider">↓ DEĞİŞTİRİLECEK ↓</span>
                            </div>
                            <div className="bg-white border-2 border-teal-300 rounded px-2 py-1.5">
                              <div className="text-[10px] font-semibold uppercase text-teal-700">İstenen doğru ürün</div>
                              <div className="text-xs font-bold text-slate-900">{ri.exchange_to.product_code}</div>
                              <div className="text-[11px] text-slate-600 leading-snug">{ri.exchange_to.product_name}</div>
                            </div>
                          </div>
                        </div>
                      )}
                      {ri.photo_paths && ri.photo_paths.length > 0 && (
                        <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
                          {ri.photo_paths.map((p, pi) => (
                            <a key={pi} href={`${API}/files/${p}?auth=${token}`} target="_blank" rel="noreferrer">
                              <img
                                src={`${API}/files/${p}?auth=${token}`}
                                alt=""
                                className="w-full aspect-square object-cover rounded border border-slate-200"
                                loading="lazy"
                              />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {req.receipt.overall_note && (
                <div className="mt-2 text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2">
                  <span className="font-medium">Genel not:</span> {req.receipt.overall_note}
                </div>
              )}
              <div className="mt-2 text-xs text-slate-500">
                Kontrol: {formatDateTime(req.receipt.checked_at)}
              </div>
            </Section>
          )}
          {req.internal_note && (
            <Section title="İç Not (PDF'te görünmez)" warn>
              <div data-testid="internal-note" className="text-sm text-slate-800 whitespace-pre-wrap">{req.internal_note}</div>
            </Section>
          )}
        </div>
          </TabsContent>
          <TabsContent value="history">
            <RequestTimeline request={req} />
          </TabsContent>
        </Tabs>
        <RequestChat request={req} onSent={load} />
        </div>

        <div className="space-y-4">
          {canModify && (
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Durum Güncelle</div>
            <Select value={req.status} onValueChange={setStatus} disabled={updating}>
              <SelectTrigger data-testid="status-select" className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`status-opt-${s}`}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {updating && <div className="mt-2 text-xs text-slate-400 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Güncelleniyor...</div>}
          </div>
          )}

          {!canModify && (
            <div data-testid="readonly-info" className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-start gap-2.5">
              <Lock className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
              <div className="text-sm">
                <div className="font-semibold text-slate-800">Sadece sahibi düzenleyebilir</div>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                  Bu talebi <span className="font-medium text-slate-700">{req.user_name}</span> oluşturdu. Görüntüleyebilir ve mesaj gönderebilirsiniz; durum güncelleme ve gönderim işlemleri yalnızca talep sahibine veya yöneticiye açıktır.
                </p>
              </div>
            </div>
          )}

          {(user?.role === "admin" || req.user_id === user?.id) && (
            <Button
              data-testid="delete-request-btn"
              variant="outline"
              className="w-full h-11 border-rose-200 text-rose-700 hover:bg-rose-50 gap-2"
              onClick={handleDelete}
            >
              <Trash2 className="h-4 w-4" /> Talebi Sil
            </Button>
          )}
        </div>
      </div>

      <MailComposeDialog open={mailOpen} onOpenChange={setMailOpen} request={req} onSent={load} />
      <B2BOrderDialog open={b2bOpen} onOpenChange={setB2bOpen} request={req} onDone={load} />
      <OrderItemsDialog
        open={orderDialogOpen}
        onOpenChange={setOrderDialogOpen}
        request={req}
        selectedIndexes={[...selected]}
        onDone={afterOrder}
      />
      <ReminderDialog open={reminderOpen} onOpenChange={setReminderOpen} request={req} onDone={load} />
      <PurchaserSelectDialog
        open={purchaserDialogOpen}
        onOpenChange={setPurchaserDialogOpen}
        request={req}
        onDone={(data) => setReq(data)}
      />
      <AlternativeOrderDialog
        open={altDialog.open}
        onOpenChange={(v) => setAltDialog((d) => ({ ...d, open: v }))}
        request={req}
        itemIndex={altDialog.index}
        onDone={afterOrder}
      />
      <LineNotesDialog
        open={notesDialog.open}
        onOpenChange={(v) => setNotesDialog((d) => ({ ...d, open: v }))}
        request={req}
        itemIndex={notesDialog.index}
        onSent={load}
      />
    </div>
  );
}

function ItemSupplierCell({ it }) {
  if (it.order_supplier_name) {
    const chMap = {
      whatsapp: { label: "WhatsApp", cls: "bg-green-100 text-green-700" },
      mail: { label: "Mail", cls: "bg-sky-100 text-sky-700" },
      b2b: { label: "B2B", cls: "bg-indigo-100 text-indigo-700" },
    };
    const c = chMap[it.order_channel];
    return (
      <div className="flex flex-col items-end gap-0.5 text-right" data-testid="item-supplier-assigned">
        <span className="text-xs font-semibold text-slate-900 leading-tight break-words">{it.order_supplier_name}</span>
        {c && <span className={`inline-flex w-fit items-center text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${c.cls}`}>{c.label}</span>}
      </div>
    );
  }
  return <div className="text-right"><span data-testid="item-supplier-pending" className="text-xs text-slate-400">Bekliyor</span></div>;
}

function Section({ title, children, warn = false }) {
  return (
    <div className={`pb-4 border-b border-slate-100 last:border-0 last:pb-0 ${warn ? "bg-amber-50/40 -mx-5 px-5 py-3 border-amber-100" : ""}`}>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3 text-sm">
      <div className="text-slate-500">{label}</div>
      <div className="col-span-2 text-slate-900 font-medium break-words">{value}</div>
    </div>
  );
}
